import React, { Suspense } from 'react';
import { performanceService } from '../services/performanceService';
import { PersianOverlayLoader } from './shared/PersianLoader';

/** Retry a dynamic import (e.g. after cache/chunk fetch failure). */
function lazyImportWithRetry<T>(
  importFn: () => Promise<{ default: T }>,
  retries = 2
): () => Promise<{ default: T }> {
  return () =>
    importFn().catch((err) => {
      if (retries <= 0) throw err;
      console.warn('Lazy import failed, retrying...', err);
      return lazyImportWithRetry(importFn, retries - 1)();
    });
}

// Lazy load main application sections with performance tracking
export const LazyDashboard = performanceService.createLazyComponent(
  () => import('./Dashboard'),
  'Dashboard',
  { preload: true }
);

export const LazyGrammar = performanceService.createLazyComponent(
  () => import('./Grammar'),
  'Grammar'
);

export const LazyQuiz = performanceService.createLazyComponent(
  () => import('./Quiz'),
  'Quiz'
);

export const LazyPronunciation = performanceService.createLazyComponent(
  () => import('./Pronunciation'),
  'Pronunciation'
);

// Learning/Course components (retry on fetch failure for CoursesSection and its deps)
export const LazyCoursesSection = performanceService.createLazyComponent(
  lazyImportWithRetry(() => import('./learning/CoursesSection')),
  'CoursesSection'
);

export const LazyCourseCatalog = performanceService.createLazyComponent(
  () => import('./learning/CourseCatalog'),
  'CourseCatalog'
);

export const LazyCourseDetail = performanceService.createLazyComponent(
  () => import('./learning/CourseDetail'),
  'CourseDetail'
);

export const LazyLessonViewer = performanceService.createLazyComponent(
  () => import('./learning/LessonViewer'),
  'LessonViewer'
);

export const LazyBookViewer = performanceService.createLazyComponent(
  () => import('./learning/BookViewer'),
  'BookViewer'
);

export const LazyLibraryPage = performanceService.createLazyComponent(
  () => import('./learning/LibraryPage'),
  'LibraryPage'
);

// Admin components - separate bundle
export const LazyAdminDashboard = performanceService.createLazyComponent(
  () => import('./admin/AdminDashboard'),
  'AdminDashboard'
);

export const LazyAdminRouter = performanceService.createLazyComponent(
  async () => {
    try {
      const module = await import('./admin/AdminRouter');
      // Handle both default and named exports
      if (module.default) {
        return module;
      }
      if (module.AdminRouter) {
        return { default: module.AdminRouter };
      }
      throw new Error('AdminRouter component not found in module');
    } catch (error) {
      console.error('Failed to load AdminRouter module:', error);
      // Re-throw to let React.lazy handle it properly
      throw error;
    }
  },
  'AdminRouter'
);

export const LazyContentManager = performanceService.createLazyComponent(
  () => import('./admin/ContentManager'),
  'ContentManager'
);

export const LazyUserManagement = performanceService.createLazyComponent(
  () => import('./admin/UserManagement'),
  'UserManagement'
);

// Authentication components
export const LazyLogin = performanceService.createLazyComponent(
  () => import('./auth/Login'),
  'Login',
  { preload: true }
);

export const LazyRegister = performanceService.createLazyComponent(
  () => import('./auth/Register'),
  'Register'
);

export const LazyAdminLogin = performanceService.createLazyComponent(
  () => import('./admin/AdminLogin'),
  'AdminLogin',
  { preload: true }
);

// Wrapper component for lazy routes with Persian loading
interface LazyRouteWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const LazyRouteWrapper: React.FC<LazyRouteWrapperProps> = ({ 
  children, 
  fallback 
}) => {
  const defaultFallback = <PersianOverlayLoader isVisible message="" />;

  return (
    <Suspense fallback={fallback || defaultFallback}>
      {children}
    </Suspense>
  );
};

// Higher-order component for performance tracking
export function withPerformanceTracking<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string
) {
  return React.forwardRef<any, P>((props, ref) => {
    return performanceService.measureComponentRender(
      componentName,
      () => <WrappedComponent {...props} ref={ref} />
    );
  });
}

// Preload critical routes
export const preloadCriticalRoutes = () => {
  // Preload dashboard and login components
  setTimeout(() => {
    import('./Dashboard').catch(console.error);
    import('./auth/Login').catch(console.error);
  }, 100);
};

// Preload admin routes when user has admin role
export const preloadAdminRoutes = () => {
  setTimeout(() => {
    import('./admin/AdminRouter').catch(console.error);
    import('./admin/AdminDashboard').catch(console.error);
  }, 100);
};

export default {
  LazyDashboard,
  LazyGrammar,
  LazyQuiz,
  LazyPronunciation,
  LazyCoursesSection,
  LazyCourseCatalog,
  LazyCourseDetail,
  LazyLessonViewer,
  LazyAdminDashboard,
  LazyAdminRouter,
  LazyContentManager,
  LazyUserManagement,
  LazyLogin,
  LazyRegister,
  LazyAdminLogin,
  LazyRouteWrapper,
  withPerformanceTracking,
  preloadCriticalRoutes,
  preloadAdminRoutes
};